import React, { useEffect, useState, useRef } from "react";
import { fetchADSearchResults } from "../../../APIs/MsGraphAPI";
import {
  AD_USER,
  SEARCH,
  DATATYPE,
  ERROR_MSG,
  EVENTS,
  EVENT_TYPES,
} from "../../../consts/Common";
import { validateEmail, validateEmailArray } from "../../../utils/campaign";
import {
  constructFullUserDetailsForDropDown,
  fetchMailIDFromUserObject,
  checkIfEmailAlreadyExist,
  checkIfEmailInUserList,
} from "../../../utils/user";

import { ChipGroup, Chip, Spinner } from "@livingdesign/react";
import { uniqBy } from "lodash";
import { trackEvent } from "../../../utils/appInsight";
import { useAppContext } from "../../../contexts/Core";
import { TextField } from "@livingdesign/react";
import { Close } from "@livingdesign/icons";

function EmailInputField({
  multiple,
  value,
  field,
  setError,
  handleChange,
  handleSaveOnChangeOrBlur,
  componentKey,
  chipProps,
  id,
  getTypedTextValue,
  setValueFromDropdownSelected,
  isLookUp = false, //this props is used to toggle between Dropdown and normal text field.
  disableFreeText = false, //this prop is used to restric the email to be one of the lookups.
  valueType = "mail", // determines what should be type of the values stored. 'mail' is the default, 'userObject' contains user data, 'upn' stores the users upn
  displayField = "displayName", // used to display user details when valueType is 'userObject',
  associateOnly = false,
  includeGroups = false,
  disabled = false,
  enableAnalytics = false,
}) {
  const [loadingResults, toggleLoadingResults] = useState(false);
  const [options, setOptions] = useState([]);
  const [emailError, toggleEmailError] = useState(false);
  const [emailLengthError, toggleEmailLengthError] = useState(false);
  const [showError, toggleShowError] = useState(false);
  const [fieldValue, setFieldValue] = useState(multiple ? [] : "");
  const [optionSelected, setOptionSelected] = useState(false);
  const timeoutId = useRef();
  const [dropDownSearchValue, setDropDownSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [multipleInputValue, setMultipleInputValue] = useState([]);
  const { pageTitle } = useAppContext();

  // All existing useEffects remain the same
  useEffect(() => {
    if (value) {
      setFieldValue(value);
      if (multiple) setMultipleInputValue(value);
    } else {
      setFieldValue(multiple ? [] : "");
    }
    if (!isLookUp) {
      validateInput(value);
    }
  }, [value, componentKey]);

  useEffect(() => {
    if (setError) {
      setError(emailError);
    }
    handleChange(value, emailError);
  }, [emailError]);

  useEffect(() => {
    if (getTypedTextValue) {
      getTypedTextValue(fieldValue);
    }
  }, [fieldValue]);

  useEffect(() => {
    if (
      typeof fieldValue !== DATATYPE.OBJECT &&
      options?.length > 0 &&
      fieldValue.length <= SEARCH.MIN_CHAR_LENGTH
    ) {
      setOptions([]);
    } else if (
      dropDownSearchValue &&
      options?.length > 0 &&
      dropDownSearchValue.length <= SEARCH.MIN_CHAR_LENGTH
    ) {
      setOptions([]);
    }
  }, [options]);

  useEffect(() => {
    if (isLookUp && !optionSelected) {
      if (typeof fieldValue === DATATYPE.STRING) {
        timeoutId.current = setTimeout(() => {
          handleSearch(fieldValue);
        }, SEARCH.DELAY_MS);
      }
    }
  }, [fieldValue]);

  useEffect(() => {
    if (isLookUp && !optionSelected) {
      timeoutId.current = setTimeout(() => {
        handleSearch(dropDownSearchValue);
      }, SEARCH.DELAY_MS);
    }
  }, [dropDownSearchValue]);

  const processInputForSearch = (inputText) => {
    if (isLookUp && timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
    if (!multiple) {
      setFieldValue(inputText);
      if (!isLookUp) validateInput(inputText);
    } else {
      const regex = /[,;(\r\n|\r|\n)]/g;
      if (regex.test(inputText)) {
        // Use a safer, linear-time regex for email validation
        const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const emailMatches = inputText.match(emailPattern);
        validateInput(emailMatches);
        handleChange([...value, ...emailMatches], emailError);
      } else {
        setDropDownSearchValue(inputText);
      }
    }
  };

  const validateInput = (input) => {
    let valid = false;
    if (multiple) {
      valid = validateEmailArray(input);
    } else {
      valid = validateEmail(input);
      if (field.max && input?.length > field.max) {
        toggleEmailLengthError(true);
      } else if (emailLengthError) {
        toggleEmailLengthError(false);
      }
    }
    toggleEmailError(!valid);
    if (multiple || valid) {
      toggleShowError(!valid);
    }
    if (!valid) setErrorMessage(ERROR_MSG.INVALID_EMAIL_ID);
    return valid;
  };

  const handleFieldChange = (_event, newValue) => {
    let recipients;
    let error = null;
    toggleShowError(false);

    if (multiple) {
      recipients = [];
      let currentValue = [...multipleInputValue, newValue];
      if (valueType === "userObject") {
        const objects = (currentValue || []).filter(
          (value) => typeof value === "object"
        );
        recipients = uniqBy(objects, AD_USER.UPN);
        setDropDownSearchValue("");
      } else {
        currentValue.forEach((recipient) => {
          let mailId;
          if (typeof recipient === DATATYPE.STRING) {
            mailId = recipient;
          } else {
            mailId =
              valueType === "mail"
                ? fetchMailIDFromUserObject(recipient)
                : constructFullUserDetailsForDropDown(recipient);
          }

          if (!mailId) return;
          if (checkIfEmailAlreadyExist(recipients, mailId)) {
            error = ERROR_MSG.EMAIL_ID_ALREADY_ADDED;
          } else if (recipients.length > SEARCH.MAX_EMAIL_LIMIT_ALLOWED) {
            error = ERROR_MSG.EMAIL_LIMIT_REACHED;
          } else if (mailId === dropDownSearchValue) {
            const emailInOptions = checkIfEmailInUserList(options, mailId);
            if (!validateEmail(mailId)) {
              error = ERROR_MSG.INVALID_EMAIL_ID;
            } else if (disableFreeText && !emailInOptions) {
              error = ERROR_MSG.EMAIL_NOT_FOUND;
            }
          }

          if (error) {
            toggleShowError(true);
            setErrorMessage(error);
            enableAnalytics &&
              trackEvent(
                EVENTS.ERRORS.GENERIC_ERROR,
                { errorMsg: ERROR_MSG.INVALID_EMAIL_ID, pageTitle: pageTitle },
                EVENT_TYPES.CREATE_MESSAGE_ERRORS
              );
          } else {
            recipients.push(mailId);
          }
        });
        setDropDownSearchValue("");
        if (!isLookUp) validateInput(recipients);
      }
      //single value
    } else {
      const mailId = newValue || fieldValue;
      if (typeof mailId === DATATYPE.STRING) {
        recipients = mailId;
        if (!optionSelected) {
          const valid = validateInput(recipients);
          !valid &&
            enableAnalytics &&
            trackEvent(
              EVENTS.ERRORS.GENERIC_ERROR,
              { errorMsg: ERROR_MSG.INVALID_EMAIL_ID, pageTitle: pageTitle },
              EVENT_TYPES.COMMON.EMAIL_VALIDATION
            );
          toggleShowError(!valid);
        }
      } else {
        recipients = constructFullUserDetailsForDropDown(mailId);
        if (setValueFromDropdownSelected) setValueFromDropdownSelected(mailId);
        setOptionSelected(true);
        setOptions([]);
      }
    }
    setMultipleInputValue(recipients);
    handleChange(recipients, emailError);
    toggleLoadingResults(false);
  };

  const getInputValue = () => {
    let inputValue;
    if (dropDownSearchValue) {
      inputValue = multiple
        ? [...(value || []), dropDownSearchValue]
        : fieldValue;
    } else {
      inputValue = multiple ? [...(value || [])] : fieldValue;
    }
    return inputValue;
  };

  // const handleFieldBlur = (event) => {
  //   const inputValue = getInputValue();
  //   handleOnBlur(event, inputValue);
  //   if (!disableFreeText && valueType !== "userObject")
  //     handleFieldChange(event, inputValue);
  //   setShowDropdown(false);
  // };

  // const handleOnBlur = (event, inputValue) => {
  //   if (!handleSaveOnChangeOrBlur) return;
  //   if (!inputValue) {
  //     inputValue = getInputValue();
  //   }
  //   if (multiple) {
  //     handleSaveOnChangeOrBlur(field.name, inputValue, fieldValue);
  //   } else {
  //     handleSaveOnChangeOrBlur(field.name, inputValue);
  //   }
  // };

  const handleSearch = async (value) => {
    if (!value) {
      setOptions([]);
    } else {
      if (value.length > SEARCH.MIN_CHAR_LENGTH) {
        try {
          const results = await fetchADSearchResults(
            value,
            associateOnly,
            includeGroups
          );
          
          // If no results and the typed value is a valid email, add it as an option
          if (!disableFreeText && (!results || results.length === 0)) {
            if (validateEmail(value)) {
              setOptions([
                {
                  isValidEmail: true,
                  email: value,
                  displayName: value,
                  mail: value
                }
              ]);
            } else {
              setOptions([SEARCH.EMPTY_RESULTS_DROPDOWN_MESSAGE]);
            }
          } else {
            setOptions(results);
          }
          toggleLoadingResults(false);
        } catch (error) {
          setOptions([SEARCH.ERROR_DROPDOWN_MESSAGE]);
          toggleLoadingResults(false);
        }
      }
    }
  };

  // Handle input changes
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    if (newValue && typeof newValue === DATATYPE.OBJECT) {
      newValue = newValue[AD_USER.MAIL] || newValue[AD_USER.UPN];
    }

    processInputForSearch(newValue);

    if (isLookUp) {
      toggleLoadingResults(
        newValue &&
          newValue.length > SEARCH.MIN_CHAR_LENGTH &&
          newValue !== fieldValue
          ? true
          : false
      );
      setShowDropdown(newValue && newValue.length > SEARCH.MIN_CHAR_LENGTH);
    }
  };

  // Handle option selection
  const handleOptionClick = (event, option) => {
    // Handle valid email option selection
    if (option?.isValidEmail) {
      const emailValue = option.email;
      if (multiple) {
        handleFieldChange(event, emailValue);
        setInputValue("");
        setDropDownSearchValue("");
      } else {
        handleFieldChange(event, emailValue);
        setInputValue(emailValue);
      }
      setShowDropdown(false);
      return;
    }

    if (multiple) {
      handleFieldChange(event, option);
      // Clear input after selection
      setInputValue("");
      setDropDownSearchValue("");
    } else {
      handleFieldChange(event, option);
      setInputValue(
        typeof option === DATATYPE.OBJECT
          ? constructFullUserDetailsForDropDown(option)
          : option
      );
    }
    setShowDropdown(false);
  };

  // Handle chip deletion for multiple values
  const handleChipDelete = (option) => {
    const currentValue = multipleInputValue;
    const newValue = currentValue.filter((chip) => chip !== option);
    setMultipleInputValue(newValue);
    handleChange(newValue, emailError);
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* Input field */}
      <div style={{ position: "relative" }}>
        <TextField
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          label={field?.label || ""}
          textFieldProps={{
            onFocus: () => isLookUp && setShowDropdown(true),
            placeholder: !value?.length ? field?.placeholder : "",
            required: field.required,
            autoComplete: "off",
          }}
          id={`${id}-EmailInputField-Input`}
          aria-label="Email Input"
          disabled={disabled}
          error={
            showError || emailLengthError
              ? showError
                ? errorMessage
                : emailLengthError
                ? field.errorText
                : ""
              : null
          }
        />

        {/* Loading indicator */}
        {loadingResults && (
          <div
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Spinner size="small" />
          </div>
        )}
      </div>

      {isLookUp && showDropdown && options?.length > 0 && (
        <div
          className="email-options-wrapper"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            zIndex: 1000, // High z-index to appear above chips
          }}
        >
          {/* Dropdown for options */}
          {options?.map((option, index) => (
            <div
              key={index}
              onClick={(e) => !option.message && handleOptionClick(e, option)}
              style={{
                padding: "8px 12px",
                cursor: option.message ? "default" : "pointer",
                backgroundColor: "white",
                borderBottom: "1px solid #eee",
                opacity: option.message ? 0.6 : 1,
                zIndex: 3,
              }}
              // onMouseEnter={(e) => !option.message && (e.target.style.backgroundColor = '#f5f5f5')}
              // onMouseLeave={(e) => !option.message && (e.target.style.backgroundColor = 'white')}
            >
              {option?.[AD_USER.DISPLAY_NAME] || option?.isValidEmail ? (
                <div style={{ backgroundColor: "inherit" }}>
                  {option?.isValidEmail 
                    ? `${option.email}`
                    : constructFullUserDetailsForDropDown(option)
                  }
                </div>
              ) : (
                <div>{option["message"] || option}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Render chips for selected values below the input in case of multiple selection */}
      {multiple && (
        <ChipGroup UNSAFE_style={{ marginTop: "8px" }}>
          {multipleInputValue.map((option, index) => (
            <Chip
              key={index}
              size="small"
              trailing={<Close onClick={() => handleChipDelete(option)} />}
            >
              {valueType === "userObject" ? option[displayField] : option}
            </Chip>
          ))}
        </ChipGroup>
      )}
    </div>
  );
}

export default EmailInputField;
